import React, { useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Flask, Sparkle } from "@phosphor-icons/react";

const SAMPLE = `Veeam Backup & Replication: Job 'fileserver01-daily' finished with status: Success
Time: 02:14 UTC
Duration: 00:23:11
Processed: 412 GB
No errors.`;

export default function TestParser() {
  const [form, setForm] = useState({
    subject: "Veeam: fileserver01-daily completed successfully",
    sender: "reports@veeam.example",
    body: SAMPLE,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async (e) => {
    e?.preventDefault();
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/test-parse", form);
      setResult(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <PageHeader title="Test parser" subtitle="Paste any backup email; see how SentryWatch classifies it" />
      <div className="p-6 grid lg:grid-cols-2 gap-6">
        <form onSubmit={run} className="sw-card" data-testid="test-parser-form">
          <header className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Flask weight="duotone" size={18} className="text-brand-cyan" />
            <h2 className="font-display font-bold tracking-tight text-sm">Input email</h2>
          </header>
          <div className="p-4 space-y-3">
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" required
              data-testid="test-subject-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan" />
            <input value={form.sender} onChange={(e) => setForm({ ...form, sender: e.target.value })} placeholder="Sender (optional)"
              data-testid="test-sender-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan" />
            <textarea rows={14} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Email body…" required
              data-testid="test-body-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-cyan" />
            <div className="flex justify-end">
              <button type="submit" disabled={loading} data-testid="test-classify-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 disabled:opacity-60 rounded-md font-semibold">
                <Sparkle size={14} weight="fill" />
                {loading ? "Classifying…" : "Classify"}
              </button>
            </div>
          </div>
        </form>

        <div className="sw-card">
          <header className="px-4 py-3 border-b border-border">
            <h2 className="font-display font-bold tracking-tight text-sm">Classification result</h2>
          </header>
          <div className="p-4 space-y-4 min-h-[400px]" data-testid="test-result">
            {!result && !loading && !error && (
              <div className="h-full flex items-center justify-center text-xs text-slate-500">
                Click &quot;Classify&quot; to see the result here.
              </div>
            )}
            {loading && <div className="text-xs text-slate-400">running classification…</div>}
            {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{error}</div>}
            {result && (
              <>
                <div className="flex items-center justify-between">
                  <StatusBadge status={result.status} size="lg" />
                  <span className="text-xs text-slate-400">
                    method: <span className="text-slate-100">{result.method}</span> · confidence: <span className="text-slate-100">{(result.confidence * 100).toFixed(0)}%</span>
                  </span>
                </div>
                <div className="border border-brand-cyan/30 rounded p-3 bg-brand-cyan/5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan mb-1">Summary</div>
                  <div className="text-sm text-slate-100">{result.summary || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">Detected jobs</div>
                  <div className="text-xs text-slate-200">{(result.detected_jobs || []).join(", ") || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">Matched rule</div>
                  <div className="text-xs text-slate-200">{result.matched_rule_id || "(none — used AI)"}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
